import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Box, InputAdornment } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { dropdownStyles } from 'styles/dropdown';

import { ModalFormSection } from 'components/common/modals';
import { ButtonV2 } from 'components/common/ui';
import { FieldV2 } from 'components/common/form-controls';

import { CONTACT_ROLES } from '@constants';
import { useSearchAddress } from '@hooks';
import parse from 'autosuggest-highlight/parse';
import validationSchema from './validationSchema';

import styles from './addServicePointModal.style';

const mapEnums = (enums, key) => {
  return (
    enums[key]?.map((item) => ({
      text: item.name,
      value: item.id,
    })) || []
  );
};

const addressListboxProps = {
  style: styles.addressListbox,
  sx: {
    '& .MuiAutocomplete-option': styles.autocompleteOption,
    '& .MuiAutocomplete-listbox': styles.autocompleteListbox,
  },
};

const addressOptionRenderer = (props, option) => {
  const matches = option.structured_formatting.main_text_matched_substrings;
  const parts = parse(
    option.structured_formatting.main_text,
    matches.map((match) => [match.offset, match.offset + match.length])
  );

  return (
    <Box component="li" sx={dropdownStyles.multiSelectItem} {...props}>
      {/* <Box component={LocationOnIcon} /> */}
      <LocationOnIcon data-testid="LocationOnIcon" fontSize="small" />
      <Box>
        {parts.map((part, index) => (
          <Box
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            component="span"
            sx={{
              fontWeight: part.highlight ? 700 : 400,
            }}
          >
            {part.text}
          </Box>
        ))}

        <Box>{option.structured_formatting.secondary_text}</Box>
      </Box>
    </Box>
  );
};

const AddServicePointForm = ({ isLoading, onCancel, onSubmit, enums }) => {
  const [brands, setBrands] = useState([]);
  const [workTypes, setWorkTypes] = useState([]);
  const [fuelTypes, setFuelTypes] = useState([]);
  const [insurances, setInsurances] = useState([]);
  const [affiliations, setAffiliations] = useState([]);
  const [banks, setBanks] = useState([]);

  const {
    options,
    address,
    fetchGooglePlaces,
    getGeocodeGooglePlace,
    handleClearAddress,
  } = useSearchAddress();

  const resolver = yupResolver(validationSchema);

  const methods = useForm({ resolver, mode: 'onChange' });

  useEffect(() => {
    methods.setValue('address', address);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address]);

  useEffect(() => {
    setBrands(mapEnums(enums, 'supportedManufacturers'));
    setWorkTypes(mapEnums(enums, 'supportedWorkTypes'));
    setFuelTypes(mapEnums(enums, 'supportedFuelTypes'));
    setInsurances(mapEnums(enums, 'supportedInsurances'));
    setAffiliations(mapEnums(enums, 'supportedProviders'));
    setBanks(mapEnums(enums, 'supportedBanks'));
  }, [enums]);

  const handleInputChange = (newInputValue) => {
    if (newInputValue) {
      fetchGooglePlaces(newInputValue);
    } else {
      methods.setValue('addressText', '');

      handleClearAddress();
    }
  };

  const handleChange = (newValue) => {
    if (newValue) {
      methods.setValue('addressText', newValue);
      getGeocodeGooglePlace({ address: newValue.description });
    }
  };

  return (
    <FormProvider {...methods}>
      <form
        id="add-service-point-form"
        onSubmit={methods.handleSubmit(onSubmit)}
      >
        <ModalFormSection>
          <Box sx={styles.row}>
            <FieldV2
              isRequired
              label="Internal ID"
              type="text"
              control={methods.control}
              name="internalId"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" style={{ display: 'flex' }}>
                    SP
                  </InputAdornment>
                ),
              }}
            />
            <FieldV2
              isRequired
              label="Service Point Name"
              type="text"
              control={methods.control}
              name="spName"
            />
          </Box>
          <Box sx={styles.row}>
            <FieldV2
              isRequired
              label="Contact Full Name"
              type="text"
              control={methods.control}
              name="contactFullName"
            />
            <FieldV2
              isRequired
              label="Email"
              type="text"
              control={methods.control}
              name="email"
            />
          </Box>
          <Box sx={styles.row}>
            <FieldV2
              isRequired
              label="Contact Role"
              control={methods.control}
              options={CONTACT_ROLES}
              type="dropdown"
              name="contactRole"
            />
            <FieldV2
              label="Address"
              type="autocomplete"
              control={methods.control}
              name="addressText"
              getOptionLabel={(option) =>
                typeof option === 'string' ? option : option.description
              }
              renderOption={addressOptionRenderer}
              ListboxProps={addressListboxProps}
              onChange={handleChange}
              onInputChange={(_, inputValue) => handleInputChange(inputValue)}
              options={options}
            />
          </Box>
          <Box sx={styles.row}>
            <FieldV2
              isRequired
              label="Telephone"
              type="text"
              control={methods.control}
              name="telephone"
            />
            <FieldV2
              label="Brands"
              type="multiselect"
              options={brands}
              control={methods.control}
              name="brands"
            />
          </Box>
          <Box sx={styles.row}>
            <FieldV2
              label="Work Types"
              type="multiselect"
              options={workTypes}
              control={methods.control}
              name="workTypes"
            />
            <FieldV2
              label="Fuel Types"
              type="multiselect"
              options={fuelTypes}
              control={methods.control}
              name="fuelTypes"
            />
          </Box>
          <Box sx={styles.row}>
            <FieldV2
              label="Insurances"
              type="multiselect"
              options={insurances}
              control={methods.control}
              name="insurances"
            />
            <FieldV2
              label="Affiliations"
              type="multiselect"
              options={affiliations}
              control={methods.control}
              name="affiliations"
            />
          </Box>
          <Box sx={styles.row}>
            <FieldV2
              label="Bank"
              type="autocomplete"
              options={banks}
              control={methods.control}
              name="bank"
            />
            <FieldV2
              label="IBAN"
              type="text"
              control={methods.control}
              name="iban"
            />
          </Box>
        </ModalFormSection>
        <Box sx={styles.footer}>
          <ButtonV2 type="button" variant="outlined" onClick={onCancel}>
            Cancel
          </ButtonV2>
          <ButtonV2
            form="add-service-point-form"
            type="submit"
            disabled={
              isLoading ||
              methods.formState.isSubmitting ||
              !methods.formState.isDirty
            }
          >
            Create
          </ButtonV2>
        </Box>
      </form>
    </FormProvider>
  );
};

export default AddServicePointForm;
